import React, { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useHTMLData, useI18N, useTaggingInformationData } from "common/hooks";
import { Helmet } from "react-helmet-async";
import Loader from "@/components/common/Loader";
import DOMPurify from "dompurify";
import { useCustomToast } from "@/hooks/useCustomToast";

export const Route = createFileRoute("/content/$slug")({
  component: Content,
});

function Content() {
  const { slug } = Route.useParams();
  const i18n = useI18N();

  const showToast = useCustomToast();

  const { setType, data, error, isLoading } = useHTMLData();
  const {
    content,
    error: isTaggingContentError,
    isLoading: isTaggingContentLoading,
  } = useTaggingInformationData();

  const [visibleBlocks, setVisibleBlocks] = useState({});

  useEffect(() => {
    if (slug) {
      setType(slug);
    }
  }, [setType, slug]);

  useEffect(() => {
    if (error) {
      showToast(error?.message, "error");
    }
  }, [error, slug]);

  useEffect(() => {
    if (isTaggingContentError) {
      showToast(isTaggingContentError?.message, "error");
    }
  }, [isTaggingContentError, slug]);

  const toggleVisibility = (id) => {
    setVisibleBlocks((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const sanitizedHtml = data?.data?.body
    ? DOMPurify.sanitize(data?.data?.body)
    : "";

  return (
    <main id="main">
      <Helmet>
        <title>{data?.data?.title} | Sociability</title>
      </Helmet>

      {(isLoading || isTaggingContentLoading) && <Loader />}

      <section className="px-4 py-6">
        <div className="max-w-[1160px] mx-auto pb-10">
          <h1 className="title-m-bold text-fg-neutral-strong mb-3">
            {data?.data?.title}
          </h1>
          {slug === "tagging-information" && (
            <h2 className="body-l-semibold leading-15 mb-5 ml-1 text-fg-neutral-strong">
              {i18n.get("pleaseClickTaggingTitle")}
            </h2>
          )}

          {slug === "tagging-information" &&
            content?.length > 0 &&
            content.map((block, ind) => {
              const blockId = block.id ? block.id : `index_${ind}`;
              return (
                <div key={blockId} className="mb-10" id={blockId}>
                  <h3
                    className="title-s-semibold text-fg-neutral-strong mb-3"
                  >
                    {block.title}
                  </h3>

                  <div className="flex items-start gap-12 p-6 rounded-xl border border-violet-200">
                    <div className="flex-grow flex flex-col gap-6">
                      <button
                        type="button"
                        id={`button_${blockId}`}
                        className="title-s-semibold text-fg-neutral-strong cursor-pointer text-left focus-blue rounded"
                        onClick={() => toggleVisibility(blockId)}
                        aria-expanded={!!visibleBlocks[blockId]}
                        aria-controls={`block_${blockId}`}
                      >
                        {block?.children[0]?.title}
                      </button>
                      {visibleBlocks[blockId] && (
                        <div
                          id={`block_${blockId}`}
                          role="region"
                          aria-labelledby={`button_${blockId}`}
                        >
                          {/* Warning: dangerouslySetInnerHTML content should be validated for accessibility.
                              Ensure all images have alt text and heading hierarchy is logical. */}
                          <div
                            className="list-container title-s-semibold text-fg-neutral-strong"
                            dangerouslySetInnerHTML={{
                              __html: block?.children[0]?.content
                                ? DOMPurify.sanitize(
                                    block?.children[0]?.content,
                                  )
                                : "",
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

          {slug !== "tagging-information" && (
            <>
              {/* Warning: dangerouslySetInnerHTML content should be validated for accessibility.
                  Ensure all images have alt text and heading hierarchy is logical. */}
              <div
                id="contentArea"
                className="title-s-semibold text-fg-neutral-strong"
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              />
            </>
          )}
        </div>
      </section>
    </main>
  );
}

export default Content;
